import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, Work, Search, Warning
} from '@mui/icons-material';
import {
  Button, Tooltip, Chip, Alert, Snackbar, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, IconButton
} from '@mui/material';

const DesignationIndex = () => {
  useSessionValidation();
  const { companyId, token, userId } = useAuth();
  const { canCreateMaster, canEditMaster, canDeleteMaster } = usePermissions();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [designations, setDesignations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [formData, setFormData] = useState({ Name: '', IsActive: true });
  const [formErrors, setFormErrors] = useState({});

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  useEffect(() => { if (companyId && token) fetchDesignations(); }, [companyId, token]);

  const fetchDesignations = async () => {
    setLoading(true);
    setLoadingMessage('Fetching designations...');
    try {
      const response = await fetchData('DesignationApi', `/${companyId}`, token);
      setDesignations(response.response_data || response || []);
    } catch (error) { console.error('Error fetching designations:', error); setDesignations([]); }
    setLoading(false);
  };

  const handleOpenModal = (action, item = null) => {
    setModalAction(action);
    setFormData(action === 'create' ? { Name: '', IsActive: true } : (item || {}));
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => { setOpenModal(false); setModalAction(''); setFormData({ Name: '', IsActive: true }); setFormErrors({}); };

  const handleSave = async () => {
    if (!formData.Name?.trim()) { setFormErrors({ Name: 'Name is required' }); return; }
    setLoading(true);
    setError('');
    try {
      const dataToSend = { ...formData, CompanyId: companyId, IsActive: formData.IsActive ?? true, CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy, ModifiedBy: modalAction === 'edit' ? userId : null };
      if (modalAction === 'create') await postData('DesignationApi', '', dataToSend, token);
      else await updateData('DesignationApi', formData.Id, dataToSend, token);
      setSuccess(`Designation ${modalAction === 'create' ? 'created' : 'updated'} successfully!`);
      handleCloseModal();
      await fetchDesignations();
    } catch (error) { console.error('Error saving:', error); setError('Failed to save designation.'); }
    setLoading(false);
  };

  const handleOpenDeleteModal = (id, name) => { setDeleteItemId(id); setDeleteItemName(name); setDeleteModalOpen(true); };
  const handleCloseDeleteModal = () => { setDeleteModalOpen(false); setDeleteItemId(null); setDeleteItemName(''); };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('DesignationApi', deleteItemId, token);
      setSuccess('Designation deleted successfully!');
      await fetchDesignations();
    } catch (error) { console.error('Error deleting:', error); setError('Failed to delete designation.'); }
    setLoading(false);
  };

  const getFilteredData = () => {
    let filtered = designations;
    if (searchTerm) filtered = filtered.filter(item => item.Name?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filterActive !== 'all') filtered = filtered.filter(item => filterActive === 'active' ? item.IsActive : !item.IsActive);
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    return { data: filtered.slice(startIndex, startIndex + rowsPerPage), totalCount: filtered.length, totalPages: Math.ceil(filtered.length / rowsPerPage), startIndex };
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData();
  const isDisabled = modalAction === 'view';

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Romans' }}>
        {loading && <LoadingOverlay message={loadingMessage} />}

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Work sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans' }}>Designation Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Search and Create Row */}
        <div style={{ marginBottom: '12px', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', minWidth: 'fit-content', paddingBottom: '4px' }}>
            {isMobile ? (
              searchExpanded ? (
                <TextField size="small" placeholder="Search designation..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} autoFocus onBlur={() => { if (!searchTerm) setSearchExpanded(false); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#7c3aed', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '12px', height: '32px', '&:hover fieldset': { borderColor: '#7c3aed' }, '&.Mui-focused fieldset': { borderColor: '#7c3aed' } } }}
                />
              ) : (
                <Tooltip title="Search" arrow>
                  <Button onClick={() => setSearchExpanded(true)} style={{ minWidth: '32px', width: '32px', height: '32px', padding: '0', borderRadius: '6px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Search style={{ color: '#7c3aed', fontSize: '18px' }} />
                  </Button>
                </Tooltip>
              )
            ) : (
              <TextField size="small" placeholder="Search designation..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#7c3aed', fontSize: '18px' }} /></InputAdornment> }}
                sx={{ flex: 1, minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '12px', height: '32px', '&:hover fieldset': { borderColor: '#7c3aed' }, '&.Mui-focused fieldset': { borderColor: '#7c3aed' } } }}
              />
            )}

            {/* Create Button */}
            <Tooltip title="Add new designation" arrow>
              <span>
                <Button variant="contained" onClick={() => handleOpenModal('create')} disabled={!canCreateMaster}
                  style={{ background: canCreateMaster ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' : '#cbd5e0', color: 'white', padding: isMobile ? '0' : '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', boxShadow: canCreateMaster ? '0 2px 8px rgba(124, 58, 237, 0.4)' : 'none', border: 'none', height: '32px', textTransform: 'none', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap', minWidth: isMobile ? '32px' : '100px', width: 'auto', marginLeft: 'auto' }}>
                  <Add style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 400 }} aria-label="designation table">
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', height: '27px' }}>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'center', height: '27px' }}>Sl No.</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'left', height: '27px' }}>Designation Name</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'center', height: '27px' }}>Status</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Work sx={{ fontSize: 18, color: '#7c3aed' }} />{item.Name || '-'}</div>
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="View" arrow><Button size="small" onClick={() => handleOpenModal('view', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button></Tooltip>
                        <Tooltip title="Edit" arrow><Button size="small" disabled={!canEditMaster} onClick={() => handleOpenModal('edit', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px', opacity: canEditMaster ? 1 : 0.4 }}>✏️</Button></Tooltip>
                        <Tooltip title="Delete" arrow><Button size="small" disabled={!canDeleteMaster} onClick={() => handleOpenDeleteModal(item.Id, item.Name)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px', opacity: canDeleteMaster ? 1 : 0.4 }}>🗑️</Button></Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No designations found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', padding: isMobile ? '8px' : '8px 12px', background: '#f8f9fa', borderRadius: '8px', gap: isMobile ? '8px' : '0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {isMobile ? `${startIndex + 1}-${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount}` : `Showing ${startIndex + 1} to ${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount} entries`}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b' }}>Rows:</span>
                  <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ padding: isMobile ? '3px 6px' : '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: isMobile ? '11px' : '13px', cursor: 'pointer', backgroundColor: 'white' }}>
                    <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: isMobile ? '3px' : '4px', justifyContent: isMobile ? 'center' : 'flex-end', flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} sx={{ minWidth: isMobile ? '32px' : '60px', fontSize: isMobile ? '11px' : '12px', textTransform: 'none', bgcolor: currentPage === 1 ? '#f1f5f9' : 'white', color: currentPage === 1 ? '#94a3b8' : '#475569', border: '1px solid #e2e8f0', padding: isMobile ? '4px 6px' : '4px 12px', '&:hover': { bgcolor: '#f8fafc' } }}>{isMobile ? '«' : 'First'}</Button>
                <Button size="small" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} sx={{ minWidth: isMobile ? '32px' : '60px', fontSize: isMobile ? '11px' : '12px', textTransform: 'none', bgcolor: currentPage === 1 ? '#f1f5f9' : 'white', color: currentPage === 1 ? '#94a3b8' : '#475569', border: '1px solid #e2e8f0', padding: isMobile ? '4px 6px' : '4px 12px', '&:hover': { bgcolor: '#f8fafc' } }}>{isMobile ? '‹' : 'Prev'}</Button>
                {[...Array(totalPages)].map((_, i) => { const page = i + 1; if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) return <Button key={page} size="small" onClick={() => setCurrentPage(page)} sx={{ minWidth: '36px', fontSize: '12px', bgcolor: currentPage === page ? '#0f3460' : 'white', color: currentPage === page ? 'white' : '#475569', border: '1px solid #e2e8f0', '&:hover': { bgcolor: currentPage === page ? '#5568d3' : '#f8fafc' } }}>{page}</Button>; else if (page === currentPage - 2 || page === currentPage + 2) return <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>; return null; })}
                <Button size="small" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} sx={{ minWidth: isMobile ? '32px' : '60px', fontSize: isMobile ? '11px' : '12px', textTransform: 'none', bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white', color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569', border: '1px solid #e2e8f0', padding: isMobile ? '4px 6px' : '4px 12px', '&:hover': { bgcolor: '#f8fafc' } }}>{isMobile ? '›' : 'Next'}</Button>
                <Button size="small" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} sx={{ minWidth: isMobile ? '32px' : '60px', fontSize: isMobile ? '11px' : '12px', textTransform: 'none', bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white', color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569', border: '1px solid #e2e8f0', padding: isMobile ? '4px 6px' : '4px 12px', '&:hover': { bgcolor: '#f8fafc' } }}>{isMobile ? '»' : 'Last'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Designation Modal */}
      <Dialog open={openModal} onClose={(e, reason) => { if (reason !== 'backdropClick') handleCloseModal(); }} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{modalAction === 'create' ? 'New' : modalAction === 'edit' ? 'Edit' : 'View'} Designation</Typography>
            <IconButton size="small" onClick={handleCloseModal}><Delete sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField size="small" fullWidth label="Designation Name *" value={formData.Name || ''} onChange={(e) => { setFormData(prev => ({ ...prev, Name: e.target.value })); setFormErrors({}); }} error={!!formErrors.Name} helperText={formErrors.Name} disabled={isDisabled} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { '& input': { padding: '8px 12px', fontSize: '0.85rem' } } }} />
          <FormControlLabel control={<Switch size="small" checked={formData.IsActive ?? true} onChange={(e) => setFormData(prev => ({ ...prev, IsActive: e.target.checked }))} disabled={isDisabled} />} label="Active" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={handleCloseModal} variant="outlined" size="small">Cancel</Button>
          {!isDisabled && <Button onClick={handleSave} variant="contained" size="small" disabled={loading}>{modalAction === 'create' ? 'Add Designation' : 'Update'}</Button>}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete <strong>{deleteItemName}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={handleCloseDeleteModal}>Cancel</Button><Button onClick={handleDelete} color="error" variant="contained">Delete</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}><Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>{success}</Alert></Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}><Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>{error}</Alert></Snackbar>
    </div>
  );
};

export default DesignationIndex;
